import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { Label, Input, Column, Field, Control, Help } from "rbx";
import moment from "moment";
import {
  CustomerNameSelect,
  CustomerNoSelect,
  DriverSelect,
  OrderStatusSelect,
  LocationSelect,
  BooleanInput
} from "../../../components/inputs";
import FilterButtons from "../../../components/FilterButtons";
import Loading from "../../../components/Loading";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const INITIAL_FILTER_STATE = {};

function OrderFilters({ filters, onFilter, onClose }) {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["NB", "BP", "VD"]
      }
    }
  });
  const [inputs, setInputs] = useState({ ...filters });
  const [errors, setErrors] = useState([]);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = {};

    if (e.target.name === "StatusRecIdRef_in") {
      updates[e.target.name] = value;
    } else if (/RecIdRef/.test(e.target.name)) {
      updates[e.target.name] = parseInt(value);
    } else if (/OrderNumber/.test(e.target.name)) {
      updates[e.target.name] = !Number.isNaN(parseInt(value))
        ? parseInt(value)
        : undefined;
    } else if (/date/gi.test(e.target.name)) {
      if (
        e.target.name === "PickupDate_gt" ||
        e.target.name === "OrderDate_gt"
      ) {
        const [str] = e.target.name.split("_");
        updates[`${str}_gt`] = value;
        updates[`${str}_lt`] = moment(value).format("YYYY-MM-DD");
      } else {
        updates[e.target.name] = value;
      }
    } else if (e.target.type === "number") {
      updates[e.target.name] = parseInt(value);
    } else {
      updates[e.target.name] = value;
    }
    const obj = { ...inputs, ...updates };
    const newInputs = Object.keys(obj).reduce((acc, curr) => {
      if (obj[curr]) {
        acc[curr] = obj[curr];
      }
      return acc;
    }, {});

    setInputs(newInputs);
  };

  const handleClose = () => onClose(onFilter(inputs));
  const handleCancel = () => onClose(onFilter(filters));
  const handleReset = () =>
    onClose(
      onFilter({
        ...INITIAL_FILTER_STATE,
        StatusRecIdRef_in: SystemCodes?.data?.SystemCodes?.map(f => f.RecId)
      })
    );

  useEffect(() => {
    if (inputs.PickupDate_gt && inputs.PickupDate_lt) {
      const start = moment(inputs.PickupDate_gt);
      const end = moment(inputs.PickupDate_lt);
      if (end.isBefore(start)) {
        setErrors(["PickupDate"]);
      } else {
        setErrors([]);
      }
    }
    if (
      SystemCodes?.data?.SystemCodes?.length > 0 &&
      !filters.StatusRecIdRef_in
    ) {
      setInputs({
        ...filters,
        StatusRecIdRef_in: SystemCodes?.data?.SystemCodes?.map(f => f.RecId)
      });
    }
  }, [SystemCodes, SystemCodes.data, filters, inputs]);

  if (!filters.StatusRecIdRef_in) {
    return <Loading />;
  }

  return (
    <div style={{ width: "100%", maxWidth: 1100 }}>
      <Column.Group>
        <Column>
          <Field>
            <Control>
              <Label>Order #</Label>
              <Input
                name="OrderNumber"
                type="text"
                value={inputs.OrderNumber}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Order # From</Label>
              <Input
                name="OrderNumber_gte"
                type="text"
                value={inputs.OrderNumber_gte}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Order # To</Label>
              <Input
                name="OrderNumber_lte"
                type="text"
                value={inputs.OrderNumber_lte}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Invoice #</Label>
              <Input
                name="InvoiceNumber"
                type="number"
                value={inputs.InvoiceNumber}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Invoice # From</Label>
              <Input
                name="InvoiceNumber_gte"
                type="number"
                value={inputs.InvoiceNumber_gt}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Invoice # To</Label>
              <Input
                name="InvoiceNumber_lte"
                type="number"
                value={inputs.InvoiceNumber_lt}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Order Date From</Label>
              <Input
                color={
                  errors.some(item => /OrderDate/gi.test(item)) ? "danger" : ""
                }
                name="OrderDate_gte"
                type="date"
                value={inputs.OrderDate_gt}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Order Date To</Label>
              <Input
                color={
                  errors.some(item => /OrderDate/gi.test(item)) ? "danger" : ""
                }
                name="OrderDate_lte"
                type="date"
                value={inputs.OrderDate_lt}
                onChange={handleChange}
              />
              <Help>
                {errors.some(item => /OrderDate/gi.test(item)) &&
                  "Must be after Order Date From"}
              </Help>
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Delivery Location ID</Label>
              <Input
                name="Del_LocationId_contains"
                type="text"
                value={inputs.Del_LocationId_contains}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Invoice Date From</Label>
              <Input
                color={
                  errors.some(item => /InvoiceDate/gi.test(item))
                    ? "danger"
                    : ""
                }
                name="InvoiceDate_gte"
                type="date"
                value={inputs.InvoiceDate_gt}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Invoice Date To</Label>
              <Input
                color={
                  errors.some(item => /InvoiceDate/gi.test(item))
                    ? "danger"
                    : ""
                }
                name="InvoiceDate_lte"
                type="date"
                value={inputs.InvoiceDate_lt}
                onChange={handleChange}
              />
              <Help>
                {errors.some(item => /InvoiceDate/gi.test(item)) &&
                  "Must be after Invoice Date From"}
              </Help>
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Driver #</Label>
              <Input
                name="PickupDriver_contains"
                type="text"
                value={inputs.PickupDriver_contains}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <DriverSelect
                icon="user"
                label="Driver"
                name="PickupDriverRecIdRef"
                value={inputs.PickupDriverRecIdRef}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Delivery Location City</Label>
              <Input
                name="Del_City_contains"
                type="text"
                value={inputs.Del_City_contains}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Invoice Batch #</Label>
              <Input
                name="InvoiceBatchNumber_contains"
                type="text"
                value={inputs.InvoiceBatchNumber_contains}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Pickup Date From</Label>
              <Input
                color={
                  errors.some(item => /PickupDate/gi.test(item)) ? "danger" : ""
                }
                name="PickupDate_gt"
                type="date"
                value={inputs.PickupDate_gt}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <Label>Pickup Date To</Label>
              <Input
                color={
                  errors.some(item => /PickupDate/gi.test(item)) ? "danger" : ""
                }
                name="PickupDate_lt"
                type="date"
                value={inputs.PickupDate_lt}
                onChange={handleChange}
              />
              <Help>
                {errors.some(item => /PickupDate/gi.test(item)) &&
                  "Must be after Pickup Date From"}
              </Help>
            </Control>
          </Field>
          <Field>
            <Control>
              <LocationSelect
                label="Pickup Location"
                name="PickupLocationRecIdRef"
                showHelp={false}
                value={inputs.PickupLocationRecIdRef}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <CustomerNameSelect
                icon="user"
                label="Customer Name"
                name="CustomerRecIdRef"
                value={inputs.CustomerRecIdRef}
                onChange={handleChange}
              />
            </Control>
          </Field>
          <Field>
            <Control>
              <CustomerNoSelect
                icon="user"
                label="Customer No."
                name="CustomerRecIdRef"
                value={inputs.CustomerRecIdRef}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
      <Field>
        <Control>
          <Label>Cancelled</Label>
          <BooleanInput
            name="Cancel"
            value={!!inputs.Cancel}
            onChange={handleChange}
          />
        </Control>
      </Field>
      <Field>
        <Control>
          <OrderStatusSelect
            value={inputs.StatusRecIdRef_in}
            onChange={handleChange}
          />
        </Control>
      </Field>

      <hr />
      <FilterButtons
        errors={errors}
        handleCancel={handleCancel}
        handleClose={handleClose}
        handleReset={handleReset}
      />
    </div>
  );
}
OrderFilters.propTypes = {
  filters: PropTypes.object,
  onFilter: PropTypes.func,
  onClose: PropTypes.func
};
export default OrderFilters;
